import { upsertProcess as upsertCASProcess } from "@/pages/caster/processes/repositories/CasterRepository";
import { upsertProcess as upsertLMFProcess } from "@/pages/lmf/lmf-processes/repositories/LMFRepository";
import {
  ButtonWithLoading,
  MesfModal,
  TimeService,
  getMomentTz,
  useShiftNavigator,
} from "@dexteel/mesf-core";
import { Button, Grid, ToggleButton, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { LazyLoading } from "../../../../../controls/LazyLoading";
import { upsertProcess as upsertEAFProcess } from "../../../../eaf/eaf-processes/repositories/EAFRepository";
import { Process } from "../../../models/ProcessGanttChart";
import type { Asset } from "../../../models/ProcessGanttChart";
import { LADLE_CAR_COLORS } from "@/pages/lmf/data-entry/constants/ladleCarColors";
import {
  readAllPlugsFromPLC,
  readProcessDataFromPLC,
} from "@/pages/lmf/data-entry/repositories/lmf.repositories";
import { ASSET_IDS } from "@/utils/constants";

const moment = getMomentTz();

type Props = {
  show: boolean;
  onHide: () => void;
  comparisonDate: Date | null;
  ganttProcesses?: Process[];
  selectedProcess: Process | null;
  selectedAsset: Asset | null;
  onError: (message: string) => void;
  onNotify: (message: string) => void;
};

export const UpsertProcess = ({
  show,
  onHide,
  comparisonDate,
  ganttProcesses = [],
  selectedProcess,
  selectedAsset,
  onError,
  onNotify,
}: Props) => {
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [isNotClosed, setIsNotClosed] = useState(false);
  const [title, setTitle] = useState<React.ReactNode>("");
  const [previousProcess, setPreviousProcess] = useState<Process | null>(null);
  const [nextProcess, setNextProcess] = useState<Process | null>(null);
  const [processCreationErrorMessage, setProcessCreationErrorMessage] =
    useState<string>("");
  const [ladleCar, setLadleCar] = useState<number>(1);

  const { shiftInfo } = useShiftNavigator();

  const startLabel =
    selectedProcess?.ProcessCode === "C" ? "Ladle Open" : "Start";
  const endLabel = selectedProcess?.ProcessCode === "C" ? "Ladle Close" : "End";

  const findPreviousAndNextProcess = (
    processes: Process[],
    timestamp: Date,
    currentProcessId?: number,
  ) => {
    let previousProcess: Process | null = null;
    let nextProcess: Process | null = null;
    for (const process of processes) {
      if (currentProcessId && process.ProcessId === currentProcessId) {
        continue;
      }
      if (process.End && process.End <= timestamp) {
        if (!previousProcess || process.End > previousProcess.End) {
          previousProcess = process;
        }
      }
    }
    const baseTime = previousProcess ? previousProcess.End : timestamp;

    for (const process of processes) {
      if (currentProcessId && process.ProcessId === currentProcessId) {
        continue;
      }
      const processStartTime =
        process.ProcessCode === "E" && process.EAFStartProcess
          ? process.EAFStartProcess
          : process.Start;
      if (processStartTime > baseTime) {
        if (!nextProcess) {
          nextProcess = process;
        } else {
          const nextProcessStartTime =
            nextProcess.ProcessCode === "E" && nextProcess.EAFStartProcess
              ? nextProcess.EAFStartProcess
              : nextProcess.Start;
          if (processStartTime < nextProcessStartTime) {
            nextProcess = process;
          }
        }
      }
    }

    return { previousProcess, nextProcess };
  };

  const {
    setValue,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Process>({
    mode: "onChange",
    defaultValues: {},
  });

  const handleDateChange = (fieldOnChange: any) => (date: any) => {
    fieldOnChange(date);
  };

  const onSubmit: SubmitHandler<Process> = async (data: Process) => {
    setIsSubmitLoading(true);
    const start = data.Start;
    const end = data.End;
    // selectedProcess and selectedAsset come from props
    const processId = data.ProcessId === 0 ? null : data.ProcessId;

    const assetId =
      selectedProcess && selectedProcess.ProcessId !== 0
        ? selectedProcess.AssetId
        : Number(selectedAsset?.id);

    if (!assetId) {
      onError("Asset ID is not defined.");
      setIsSubmitLoading(false);
      return;
    }

    const isEAF = assetId === ASSET_IDS.EAF;
    const isLMF = assetId === ASSET_IDS.LMF;
    const isCreating = processId === null;

    const response = isEAF
      ? await upsertEAFProcess(processId, start, end, true, assetId)
      : isLMF
        ? await upsertLMFProcess(
            processId,
            start,
            end,
            true,
            assetId,
            isCreating ? ladleCar : null,
            isCreating ? true : undefined,
          )
        : await upsertCASProcess(processId, start, end, true, assetId);

    if (response.ok) {
      if (isCreating && isLMF) {
        const successResponse = response as {
          ok: true;
          data: { returnValue: number };
        };
        const newProcessId = Number(successResponse.data.returnValue);
        try {
          await Promise.all([
            readAllPlugsFromPLC({ ProcessId: newProcessId }),
            readProcessDataFromPLC({ ProcessId: newProcessId }),
          ]);
        } catch {
          // PLC reads are best-effort — process was already created successfully
        }
      }
      onNotify(`Process was ${isNew ? "created" : "edited"} successfully`);
    } else {
      onError(response?.message);
    }
    setIsSubmitLoading(false);
    onHide();
  };

  useEffect(() => {
    if (show) {
      let cancelled = false;
      setIsSubmitLoading(false);
      setProcessCreationErrorMessage("");
      (async () => {
        setIsLoading(true);
        const process = selectedProcess;
        if (cancelled) return;
        if (!process) return;

        if (process.ProcessId === 0) {
          setTitle("NEW PROCESS");
          setIsNew(true);
          setLadleCar(1);

          if (selectedAsset && comparisonDate) {
            const filteredProcesses = ganttProcesses.filter(
              (p) => p.AssetId === Number(selectedAsset?.id),
            );
            const { previousProcess, nextProcess } = findPreviousAndNextProcess(
              filteredProcesses,
              comparisonDate,
            );
            setPreviousProcess(previousProcess);
            setNextProcess(nextProcess);
            let startTime;
            let endTime;
            if (previousProcess && previousProcess.End) {
              startTime = moment(previousProcess.End)
                .add(2, "minutes")
                .toDate();
            } else {
              const nextProcessTime =
                selectedAsset?.id === ASSET_IDS.EAF.toString() &&
                nextProcess?.EAFStartProcess
                  ? nextProcess.EAFStartProcess
                  : nextProcess?.Start;
              startTime =
                moment(nextProcessTime).subtract(1, "hour").toDate() ||
                moment().toDate();
            }

            if (nextProcess && nextProcess.Start) {
              const nextProcessTime =
                nextProcess.ProcessCode === "E" && nextProcess.EAFStartProcess
                  ? nextProcess.EAFStartProcess
                  : nextProcess.Start;
              endTime = moment(nextProcessTime).subtract(2, "minutes").toDate();
            } else {
              endTime = moment(startTime).add(1, "hour").toDate();
            }

            if (
              previousProcess &&
              previousProcess.End &&
              nextProcess &&
              nextProcess.Start
            ) {
              const nextProcessTime =
                nextProcess.ProcessCode === "E" && nextProcess.EAFStartProcess
                  ? nextProcess.EAFStartProcess
                  : nextProcess.Start;
              const timeGap = moment(nextProcessTime).diff(
                moment(previousProcess.End),
                "minutes",
              );

              if (timeGap < 4) {
                setProcessCreationErrorMessage(
                  "The time between processes is too small to create a new one in the selected position.",
                );
                setValue("Start", previousProcess.End);
                setValue("End", nextProcessTime);
                setIsLoading(false);
                return;
              }
            }

            setValue("ProcessId", 0);
            setValue("Start", startTime);
            setValue("End", endTime);
          }
        } else {
          setTitle(
            <>
              EDIT{" "}
              {process.ProcessCode === "C"
                ? "CASTER"
                : process.ProcessCode === "L"
                  ? "LMF"
                  : "EAF"}{" "}
              PROCESS
              {process.HeatName ? (
                <> | HEAT: {process.HeatName}</>
              ) : (
                <>
                  {" "}
                  | <span style={{ color: "red" }}>UNLINKED HEAT</span>
                </>
              )}
            </>,
          );
          setIsNew(false);
          if (process.Start) {
            // Find processes for the same asset from ganttData
            const filteredProcesses = ganttProcesses.filter(
              (p) => p.AssetId === process.AssetId,
            );
            const { previousProcess, nextProcess } = findPreviousAndNextProcess(
              filteredProcesses,
              process.Start,
              process.ProcessId,
            );
            setPreviousProcess(previousProcess);
            setNextProcess(nextProcess);
          }

          setValue("ProcessId", process.ProcessId);
          const startTime =
            process.ProcessCode === "E" && process.EAFStartProcess
              ? moment.utc(process.EAFStartProcess).toDate()
              : moment.utc(process.Start).toDate();
          setValue("Start", startTime);
          setValue("End", moment.utc(process.End).toDate());
        }
        if (cancelled) return;
        if (process.End === null) setIsNotClosed(true);
        setIsLoading(false);
      })();
      return () => {
        cancelled = true;
      };
    } else {
      reset();
      setIsNotClosed(false);
      setProcessCreationErrorMessage("");
    }
  }, [show]);

  useEffect(() => {
    if (isNotClosed) {
      setValue("End", new Date());
    }
  }, [isNotClosed]);

  return (
    <>
      <Grid container>
        <Grid item>
          <MesfModal
            maxWidth="sm"
            open={show}
            handleClose={() => {
              onHide();
            }}
            id="upsertProcess"
            title={title as any}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <MesfModal.Content style={{ padding: "15px 30px" }}>
                {isLoading && (
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    style={{ minHeight: "300px" }}
                  >
                    <LazyLoading />
                  </Grid>
                )}
                {!isLoading && (
                  <>
                    {isNew && Number(selectedAsset?.id) === ASSET_IDS.LMF && (
                      <Grid container spacing={1} style={{ marginBottom: 10 }}>
                        <Grid item xs={12}>
                          <Typography
                            variant="subtitle2"
                            style={{ marginBottom: 4 }}
                          >
                            Ladle Car
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <ToggleButton
                            value={1}
                            selected={ladleCar === 1}
                            onChange={() => setLadleCar(1)}
                            fullWidth
                            sx={{
                              height: 44,
                              fontWeight: 600,
                              backgroundColor:
                                ladleCar === 1
                                  ? `${LADLE_CAR_COLORS.lc1.border} !important`
                                  : LADLE_CAR_COLORS.lc1.bg,
                              color: "#000",
                              border: `1px solid ${LADLE_CAR_COLORS.lc1.border}`,
                              borderRadius: "4px !important",
                              "&:hover": {
                                backgroundColor: LADLE_CAR_COLORS.lc1.hover,
                              },
                            }}
                          >
                            LC 1
                          </ToggleButton>
                        </Grid>
                        <Grid item xs={6}>
                          <ToggleButton
                            value={2}
                            selected={ladleCar === 2}
                            onChange={() => setLadleCar(2)}
                            fullWidth
                            sx={{
                              height: 44,
                              fontWeight: 600,
                              backgroundColor:
                                ladleCar === 2
                                  ? `${LADLE_CAR_COLORS.lc2.border} !important`
                                  : LADLE_CAR_COLORS.lc2.bg,
                              color: "#000",
                              border: `1px solid ${LADLE_CAR_COLORS.lc2.border}`,
                              borderRadius: "4px !important",
                              "&:hover": {
                                backgroundColor: LADLE_CAR_COLORS.lc2.hover,
                              },
                            }}
                          >
                            LC 2
                          </ToggleButton>
                        </Grid>
                      </Grid>
                    )}
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="Start"
                          control={control}
                          rules={{
                            required: "The Start field is required",
                            validate: {
                              notAfterEnd: (value) => {
                                const endValue = watch("End");
                                if (!endValue) return true;
                                return (
                                  moment(value).isBefore(moment(endValue)) ||
                                  "Start must be before End"
                                );
                              },
                              notBeforePreviousEnd: (value) => {
                                const start = previousProcess?.End;
                                if (!start) return true;
                                return (
                                  moment(value).isSameOrAfter(moment(start)) ||
                                  "Start cannot be less than the End of the Previous Process"
                                );
                              },
                              notAfterNextStart: (value) => {
                                const nextProcessTime =
                                  nextProcess?.ProcessCode === "E" &&
                                  nextProcess?.EAFStartProcess
                                    ? nextProcess.EAFStartProcess
                                    : nextProcess?.Start;
                                if (!nextProcessTime) return true;
                                return (
                                  moment(value).isSameOrBefore(
                                    moment(nextProcessTime),
                                  ) ||
                                  "Start cannot be greater than the Start of the Next Process"
                                );
                              },
                            },
                          }}
                          render={({ field }) => (
                            <DateTimePicker
                              label={startLabel}
                              timezone={TimeService.getInstance().getServerTimeZone()}
                              format="MM/DD/YYYY HH:mm"
                              disabled={!!processCreationErrorMessage}
                              timeSteps={{ minutes: 1 }}
                              value={moment(field.value) as any}
                              onChange={handleDateChange(field.onChange)}
                              inputRef={field.ref}
                              slotProps={{
                                textField: {
                                  variant: "outlined",
                                  error: !!errors.Start,
                                },
                              }}
                            />
                          )}
                        />
                        {errors.Start && (
                          <p style={{ color: "red", fontSize: 12 }}>
                            {errors.Start?.message}
                          </p>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="End"
                          control={control}
                          rules={{
                            required: !isNotClosed
                              ? "The End field is required"
                              : false,
                            validate: {
                              notBeforeStart: (value) => {
                                const startValue = watch("Start");
                                if (!startValue) return true;
                                return (
                                  moment(value).isAfter(moment(startValue)) ||
                                  "End must be after Start"
                                );
                              },
                              notAfterNextStart: (value) => {
                                const nextProcessTime =
                                  nextProcess?.ProcessCode === "E" &&
                                  nextProcess?.EAFStartProcess
                                    ? nextProcess.EAFStartProcess
                                    : nextProcess?.Start;
                                if (!nextProcessTime) return true;
                                return (
                                  moment(value).isSameOrBefore(
                                    moment(nextProcessTime),
                                  ) ||
                                  "End cannot be greater than the Start of the Next Process"
                                );
                              },
                            },
                          }}
                          render={({ field }) => (
                            <DateTimePicker
                              label={endLabel}
                              timezone={TimeService.getInstance().getServerTimeZone()}
                              format="MM/DD/YYYY HH:mm"
                              disabled={!!processCreationErrorMessage}
                              timeSteps={{ minutes: 1 }}
                              value={moment(field.value) as any}
                              onChange={field.onChange}
                              inputRef={field.ref}
                              slotProps={{
                                textField: {
                                  variant: "outlined",
                                  error: !!errors.End,
                                },
                              }}
                            />
                          )}
                        />
                        {errors.End && (
                          <p style={{ color: "red", fontSize: 12 }}>
                            {errors.End?.message}
                          </p>
                        )}
                      </Grid>
                    </Grid>
                    {processCreationErrorMessage && (
                      <p style={{ color: "red", fontSize: 18, marginTop: 10 }}>
                        {processCreationErrorMessage}
                      </p>
                    )}
                    {/* <Grid item xs={12} md={6} style={{ marginTop: 20 }}>
												<FormControlLabel
													control={
														(
															<Checkbox
																{...register("IsOpen")}
																color="primary"
																checked={isNotClosed}
																onChange={(event) =>
																	setIsNotClosed(event.target.checked)
																}
															/>
														) as any
													}
													label="Ladle is not Closed"
												/>
											</Grid> */}
                  </>
                )}
              </MesfModal.Content>
              <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
                <Grid container spacing={2} justifyContent="flex-end">
                  <Grid item md={3} xs={12} style={{ margin: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="inherit"
                      onClick={() => onHide()}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item md={3} xs={12} style={{ margin: 0 }}>
                    {!!processCreationErrorMessage ? (
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={!!processCreationErrorMessage}
                      >
                        Save
                      </Button>
                    ) : (
                      <ButtonWithLoading
                        fullWidth
                        variant="contained"
                        color="primary"
                        type="submit"
                        isLoading={isSubmitLoading}
                      >
                        Save
                      </ButtonWithLoading>
                    )}
                  </Grid>
                </Grid>
              </MesfModal.Actions>
            </form>
          </MesfModal>
        </Grid>
      </Grid>
    </>
  );
};
